import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Camera, MapPin, RefreshCw, ShieldAlert, LogIn, LogOut, Check, AlertTriangle,
  Trash2, CloudUpload, Clock, ChevronLeft,
} from "lucide-react";
import BrandMark from "../components/BrandMark";
import InstallPrompt from "../components/InstallPrompt";
import PwaStatusBar from "../components/PwaStatusBar";
import ConfidenceBadge from "../components/ConfidenceBadge";
import PwaEnroll from "../components/PwaEnroll";
import { API_BASE, getPwaToken, getPwaMeta, clearPwaSession } from "../lib/api";
import { capturePunch, trySync } from "../lib/pwaSync";
import { listAttendance, deleteAttendance, clearSynced } from "../lib/db";

const STEPS = {
  ENROLL: "ENROLL",
  PERMISSIONS: "PERMISSIONS",
  SELECT: "SELECT",
  CAPTURE: "CAPTURE",
  RESULT: "RESULT",
  QUEUE: "QUEUE",
};

export default function Presence() {
  const initialMeta = getPwaMeta();
  const hasToken = Boolean(getPwaToken() && initialMeta?.employee);
  const [step, setStep] = useState(hasToken ? STEPS.PERMISSIONS : STEPS.ENROLL);
  const [employees, setEmployees] = useState([]);
  const [outlet, setOutlet] = useState(initialMeta?.outlet || null);
  const [enrolledEmp, setEnrolledEmp] = useState(initialMeta?.employee || null);
  const [attendanceType, setAttendanceType] = useState("CHECK_IN");
  const [camState, setCamState] = useState("idle");
  const [geoState, setGeoState] = useState("idle");
  const [gps, setGps] = useState(null);
  const [selfie, setSelfie] = useState(null);
  const [result, setResult] = useState(null);
  const [queue, setQueue] = useState([]);
  const [busy, setBusy] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  // Stable device fingerprint (also used as hash key on server).
  const fingerprint = useMemo(() => {
    let fp = localStorage.getItem("presence_fp");
    if (!fp) {
      fp = "pwa_" + (crypto.randomUUID ? crypto.randomUUID()
              : String(Date.now()) + Math.random().toString(36).slice(2));
      localStorage.setItem("presence_fp", fp);
    }
    return fp;
  }, []);

  // Bootstrap outlet + employees (use device bootstrap endpoint for demo).
  useEffect(() => {
    axios.get(`${API_BASE}/terminal/demo-bootstrap`)
      .then((r) => {
        if (!outlet) setOutlet(r.data.outlet);
        // reuse public employees via device token
        localStorage.setItem("device_token", r.data.device_token);
        return axios.get(`${API_BASE}/employees/public`, {
          headers: { Authorization: `Bearer ${r.data.device_token}` },
        });
      })
      .then((r) => setEmployees(r.data))
      .catch(() => {});
  }, []);

  // Read ?action=in|out from URL for app shortcut
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const a = p.get("action");
    if (a === "in") setAttendanceType("CHECK_IN");
    if (a === "out") setAttendanceType("CHECK_OUT");
  }, []);

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };
  useEffect(() => () => stopCamera(), []);

  const requestCamera = async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" }, audio: false });
      // Release — we just wanted to ensure permission, we'll request again at capture.
      s.getTracks().forEach((t) => t.stop());
      setCamState("granted");
    } catch (e) {
      setCamState(e?.name === "NotAllowedError" ? "denied" : "error");
    }
  };

  const requestGeo = () => {
    if (!navigator.geolocation) { setGeoState("unavailable"); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy });
        setGeoState("granted");
      },
      (err) => setGeoState(err.code === 1 ? "denied" : "unavailable"),
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  const proceedFromPerms = () => {
    setStep(STEPS.SELECT);
  };

  const openCapture = async () => {
    setSelfie(null);
    setStep(STEPS.CAPTURE);
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user", width: 640, height: 640 }, audio: false });
      streamRef.current = s;
      if (videoRef.current) {
        videoRef.current.srcObject = s;
        await videoRef.current.play().catch(() => {});
      }
    } catch (e) {
      setCamState(e?.name === "NotAllowedError" ? "denied" : "error");
    }
  };

  const captureAndSubmit = async () => {
    setBusy(true);
    // Snap frame from video to canvas
    let dataUrl = null;
    const v = videoRef.current;
    if (v && v.videoWidth > 0) {
      const size = Math.min(v.videoWidth, v.videoHeight);
      const canvas = document.createElement("canvas");
      canvas.width = size; canvas.height = size;
      const sx = (v.videoWidth - size) / 2;
      const sy = (v.videoHeight - size) / 2;
      canvas.getContext("2d").drawImage(v, sx, sy, size, size, 0, 0, size, size);
      dataUrl = canvas.toDataURL("image/jpeg", 0.65);
    }
    setSelfie(dataUrl);
    stopCamera();

    // Refresh GPS best-effort
    let freshGps = gps;
    if (navigator.geolocation) {
      await new Promise((resolve) => {
        navigator.geolocation.getCurrentPosition(
          (pos) => { freshGps = { lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }; resolve(); },
          () => resolve(),
          { enableHighAccuracy: true, timeout: 4000, maximumAge: 10000 }
        );
      });
    }
    setGps(freshGps);

    const res = await capturePunch({
      employee_id: enrolledEmp.id,
      outlet_id: outlet?.id,
      attendance_type: attendanceType,
      selfie_blob: dataUrl,
      gps: freshGps,
    });

    setResult({
      ok: res.ok,
      record: res.record,
      error: res.error,
      employee: enrolledEmp,
    });
    setStep(STEPS.RESULT);
    setBusy(false);
  };

  const back = () => {
    stopCamera();
    setStep(STEPS.SELECT);
    setSelfie(null);
    setResult(null);
  };

  const unenroll = () => {
    clearPwaSession();
    setEnrolledEmp(null);
    setStep(STEPS.ENROLL);
  };

  const openQueue = async () => {
    const all = await listAttendance();
    setQueue(all.sort((a, b) => b.captured_at_device.localeCompare(a.captured_at_device)));
    setStep(STEPS.QUEUE);
  };

  return (
    <div
      className="min-h-screen bg-white text-neutral-900"
      style={{ fontFamily: "IBM Plex Sans, sans-serif" }}
      data-testid="presence-root"
    >
      {/* Header */}
      <header className="bg-[#0B0F14] text-white">
        <div className="px-4 py-3 flex items-center gap-3">
          {step !== STEPS.PERMISSIONS && step !== STEPS.SELECT && step !== STEPS.ENROLL && (
            <button onClick={back} aria-label="back" data-testid="presence-back" className="mr-1">
              <ChevronLeft size={22} />
            </button>
          )}
          <BrandMark size={22} inverted />
          <div className="leading-tight">
            <div className="mono-label text-neutral-400" style={{ fontSize: 9 }}>PRESENCE · PWA</div>
            <div className="font-heading font-bold text-sm">{outlet?.name || "Loading…"}</div>
          </div>
          <div className="ml-auto flex gap-2">
            {enrolledEmp && (
              <button
                data-testid="btn-unenroll"
                onClick={unenroll}
                className="h-9 px-3 border border-neutral-700 font-mono text-[10px] uppercase tracking-wider"
                title="Clear device enrolment"
              >
                Unpair
              </button>
            )}
            <button
              onClick={openQueue}
              className="h-9 px-3 border border-neutral-700 font-mono text-[10px] uppercase tracking-wider inline-flex items-center gap-1"
              data-testid="open-queue"
            >
              <Clock size={12} /> Queue
            </button>
          </div>
        </div>
        <div className="px-4 pb-3">
          <PwaStatusBar />
        </div>
      </header>

      {/* Truth contract banner */}
      <div className="bg-[#FEF9C3] border-b border-[#EAB308] px-4 py-2 text-center">
        <div className="mono-label text-[#854D0E]" style={{ fontSize: 9 }}>
          DEVICE CAPTURE IS EVIDENCE · PEOPLE ENGINE DECIDES TRUTH
        </div>
      </div>

      <main className="max-w-lg mx-auto pb-24">
        {step === STEPS.ENROLL && (
          <PwaEnroll
            employees={employees}
            fingerprint={fingerprint}
            onEnrolled={(data) => { setEnrolledEmp(data.employee); setOutlet(data.outlet); setStep(STEPS.PERMISSIONS); }}
            onCancel={() => window.history.back()}
          />
        )}
        {step === STEPS.PERMISSIONS && (
          <PermissionsStep
            camState={camState}
            geoState={geoState}
            onCam={requestCamera}
            onGeo={requestGeo}
            onContinue={proceedFromPerms}
          />
        )}
        {step === STEPS.SELECT && (
          <EnrolledHomeStep
            enrolledEmp={enrolledEmp}
            attendanceType={attendanceType}
            setAttendanceType={setAttendanceType}
            onStart={openCapture}
            onViewQueue={openQueue}
          />
        )}
        {step === STEPS.CAPTURE && (
          <CaptureStep
            videoRef={videoRef}
            employee={enrolledEmp}
            attendanceType={attendanceType}
            gps={gps}
            camState={camState}
            onCapture={captureAndSubmit}
            busy={busy}
          />
        )}
        {step === STEPS.RESULT && (
          <ResultStep
            result={result}
            selfie={selfie}
            onDone={() => { setResult(null); setStep(STEPS.SELECT); }}
          />
        )}
        {step === STEPS.QUEUE && (
          <QueueStep
            queue={queue}
            onRetry={async (id) => { await trySync(id); openQueue(); }}
            onDelete={async (id) => { await deleteAttendance(id); openQueue(); }}
            onClearSynced={async () => { await clearSynced(); openQueue(); }}
            onClose={() => setStep(STEPS.SELECT)}
          />
        )}
      </main>

      <InstallPrompt />
    </div>
  );
}

/* ---------- STEPS ---------- */

function PermissionsStep({ camState, geoState, onCam, onGeo, onContinue }) {
  const bothReady = camState === "granted" && (geoState === "granted" || geoState === "unavailable");
  const camDenied = camState === "denied" || camState === "error";
  return (
    <div className="p-5">
      <div className="mono-label">PRE-FLIGHT</div>
      <h1 className="font-heading text-3xl font-black tracking-tighter mt-2">Grant capture permissions.</h1>
      <p className="text-neutral-600 mt-2 text-sm leading-relaxed">
        Presence captures a selfie + location stamp as evidence of your punch. It is only used by the
        People Engine for attendance verification.
      </p>
      <div className="mt-6 space-y-3">
        <PermCard
          icon={Camera}
          label="Camera"
          desc="Front-facing selfie at punch time."
          state={camState}
          onAsk={onCam}
          testid="perm-cam"
        />
        <PermCard
          icon={MapPin}
          label="Location"
          desc="Proves you are at the outlet."
          state={geoState}
          onAsk={onGeo}
          testid="perm-geo"
        />
      </div>
      {camDenied && (
        <div data-testid="perm-cam-help" className="mt-4 border border-[#EF4444] bg-[#FEE2E2] text-[#991B1B] p-3 text-xs">
          Camera permission is blocked. Allow camera access in your browser settings, then reload.
        </div>
      )}
      <button
        data-testid="btn-perms-continue"
        onClick={onContinue}
        disabled={!bothReady && camState !== "denied"}
        className="mt-6 w-full h-14 bg-black text-white font-mono text-sm uppercase tracking-wider disabled:opacity-50"
      >
        Continue
      </button>
    </div>
  );
}

function PermCard({ icon: Icon, label, desc, state, onAsk, testid }) {
  const tone =
    state === "granted" ? "border-[#22C55E] bg-[#DCFCE7]"
    : state === "denied" || state === "error" ? "border-[#EF4444] bg-[#FEE2E2]"
    : state === "unavailable" ? "border-neutral-400 bg-neutral-100"
    : "border-neutral-300 bg-white";
  const stateText = {
    idle: "NOT ASKED", granted: "GRANTED", denied: "BLOCKED", error: "ERROR", unavailable: "N/A",
  }[state] || "NOT ASKED";
  return (
    <div className={`border ${tone} p-4 flex items-center gap-3`} data-testid={testid}>
      <Icon size={22} strokeWidth={1.5} />
      <div className="flex-1 min-w-0">
        <div className="font-semibold">{label}</div>
        <div className="text-xs text-neutral-600">{desc}</div>
      </div>
      <div className="mono-label" style={{ fontSize: 10 }}>{stateText}</div>
      <button
        data-testid={`${testid}-ask`}
        onClick={onAsk}
        disabled={state === "granted"}
        className="h-9 px-3 border border-current font-mono text-[10px] uppercase tracking-wider disabled:opacity-40"
      >
        {state === "granted" ? "OK" : "Allow"}
      </button>
    </div>
  );
}

function EnrolledHomeStep({ enrolledEmp, attendanceType, setAttendanceType, onStart, onViewQueue }) {
  return (
    <div className="p-4 pb-8">
      <div className="mono-label mt-2">ENROLLED IDENTITY</div>
      <div className="mt-2 border border-neutral-900 bg-white p-4 flex items-center gap-3">
        <img src={enrolledEmp?.photo_url} alt="" className="w-16 h-16 object-cover grayscale border border-neutral-200" />
        <div className="flex-1 min-w-0">
          <div className="font-heading font-bold text-lg tracking-tight">{enrolledEmp?.name}</div>
          <div className="mono-label" style={{ fontSize: 10 }}>{enrolledEmp?.code} · {enrolledEmp?.role}</div>
        </div>
      </div>
      <p className="text-neutral-600 mt-3 text-sm leading-relaxed">
        Only you can punch from this device. Unpair in the top bar to hand over.
      </p>

      <div className="mt-5 grid grid-cols-2 gap-2">
        <button
          data-testid="btn-check-in"
          onClick={() => setAttendanceType("CHECK_IN")}
          className={`h-14 border font-mono text-xs uppercase tracking-wider inline-flex items-center justify-center gap-2 ${
            attendanceType === "CHECK_IN" ? "bg-black text-white border-black" : "border-neutral-300"
          }`}
        >
          <LogIn size={16} /> Check in
        </button>
        <button
          data-testid="btn-check-out"
          onClick={() => setAttendanceType("CHECK_OUT")}
          className={`h-14 border font-mono text-xs uppercase tracking-wider inline-flex items-center justify-center gap-2 ${
            attendanceType === "CHECK_OUT" ? "bg-black text-white border-black" : "border-neutral-300"
          }`}
        >
          <LogOut size={16} /> Check out
        </button>
      </div>

      <button
        data-testid="btn-start-capture"
        onClick={onStart}
        className="mt-6 w-full h-16 bg-black text-white font-heading font-bold uppercase tracking-wider text-lg inline-flex items-center justify-center gap-2"
      >
        <Camera size={20} /> Start capture
      </button>

      <button
        data-testid="btn-view-queue"
        onClick={onViewQueue}
        className="mt-3 w-full h-11 border border-neutral-300 font-mono text-xs uppercase tracking-wider inline-flex items-center justify-center gap-2"
      >
        <Clock size={14} /> View local queue
      </button>
    </div>
  );
}

function CaptureStep({ videoRef, employee, attendanceType, gps, camState, onCapture, busy }) {
  return (
    <div className="p-4">
      <div className="mono-label">STEP 02 / 02</div>
      <h2 className="font-heading text-2xl font-black tracking-tighter mt-1">
        {attendanceType === "CHECK_IN" ? "Checking in." : "Checking out."}
      </h2>
      <div className="mt-3 flex items-center gap-3">
        <img src={employee?.photo_url} alt="" className="w-12 h-12 object-cover grayscale border border-neutral-200" />
        <div>
          <div className="font-semibold">{employee?.name}</div>
          <div className="mono-label" style={{ fontSize: 9 }}>{employee?.code}</div>
        </div>
      </div>

      <div className="mt-4 relative aspect-square bg-black overflow-hidden border border-neutral-900">
        <video ref={videoRef} playsInline muted className="w-full h-full object-cover" />
        <CornerBrackets />
        <div className="absolute inset-x-0 top-0 py-2 text-center">
          <span className="mono-label text-white/80">FRAME YOUR FACE</span>
        </div>
        {camState === "denied" && (
          <div className="absolute inset-0 bg-black/80 text-white flex flex-col items-center justify-center p-6 text-center">
            <ShieldAlert size={28} className="text-red-400" />
            <div className="mono-label mt-2">CAMERA BLOCKED</div>
            <p className="text-xs text-neutral-300 mt-2">
              Enable camera permission in browser settings, then reload to continue.
            </p>
          </div>
        )}
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2 text-xs font-mono text-neutral-600">
        <div><span className="text-neutral-400">type</span> {attendanceType}</div>
        <div><span className="text-neutral-400">outlet</span> {employee?.outlet_id || "—"}</div>
        <div>
          <span className="text-neutral-400">gps</span>{" "}
          {gps ? `${gps.lat.toFixed(4)}, ${gps.lng.toFixed(4)} ±${Math.round(gps.accuracy)}m` : "—"}
        </div>
        <div><span className="text-neutral-400">time</span> {new Date().toLocaleTimeString("en-GB")}</div>
      </div>

      <button
        data-testid="btn-capture-punch"
        onClick={onCapture}
        disabled={busy || camState === "denied"}
        className="mt-6 w-full h-16 bg-black text-white font-heading font-bold uppercase tracking-wider text-lg inline-flex items-center justify-center gap-2 disabled:opacity-50"
      >
        <Camera size={20} /> {busy ? "Capturing…" : `Capture ${attendanceType === "CHECK_IN" ? "check-in" : "check-out"}`}
      </button>
    </div>
  );
}

function ResultStep({ result, selfie, onDone }) {
  const rec = result?.record || {};
  const status = rec.sync_status || "PENDING";
  const server = rec.server_response || null;

  let toneClass = "border-neutral-400 bg-neutral-100 text-neutral-800";
  let title = "Captured offline.";
  let sub = "Pending verification. We'll sync when online.";

  if (status === "SYNCED") {
    if (server?.status === "ACCEPTED") {
      toneClass = "border-[#EAB308] bg-[#FEF9C3] text-[#854D0E]";
      title = "Captured.";
      // Mobile captures are stored by the Presence server but are not yet
      // forwarded to People Engine — never imply PE has the punch.
      sub = "Saved on the Presence server · not yet delivered to People Engine.";
    } else if (server?.status === "REVIEW") {
      toneClass = "border-[#EAB308] bg-[#FEF9C3] text-[#854D0E]";
      title = "Captured · needs review.";
      sub = server?.message || "Your manager will resolve this in People Engine.";
    } else if (server?.status === "REJECTED") {
      toneClass = "border-[#EF4444] bg-[#FEE2E2] text-[#991B1B]";
      title = "Rejected.";
      sub = server?.message || "Please try again. If it keeps failing, ask your manager.";
    }
  } else if (status === "PENDING") {
    toneClass = "border-[#EAB308] bg-[#FEF9C3] text-[#854D0E]";
    title = "Captured.";
    sub = "Saved on this device · waiting to reach the Presence server.";
  } else if (status === "FAILED") {
    toneClass = "border-[#EF4444] bg-[#FEE2E2] text-[#991B1B]";
    title = "Sync failed.";
    sub = rec.last_error || "We'll retry automatically.";
  }

  return (
    <div className="p-4">
      <div className={`border ${toneClass} p-5`} data-testid="presence-result">
        <div className="mono-label" style={{ fontSize: 10 }}>{status}</div>
        <h2 className="font-heading text-3xl font-black tracking-tighter mt-2">{title}</h2>
        <p className="mt-2 text-sm">{sub}</p>

        <div className="mt-4 flex items-center gap-3">
          {selfie && (
            <img src={selfie} alt="selfie" className="w-24 h-24 object-cover border border-neutral-900 grayscale" />
          )}
          <div className="flex-1">
            {server?.confidence_tier && (
              <ConfidenceBadge tier={server.confidence_tier} score={server.confidence_score} />
            )}
            <div className="mt-2 text-xs font-mono opacity-80">
              <div>local_id: {rec.local_id}</div>
              <div>captured: {new Date(rec.captured_at_device).toLocaleTimeString("en-GB")}</div>
              {rec.gps_lat != null && <div>gps: {rec.gps_lat.toFixed(4)}, {rec.gps_lng.toFixed(4)}</div>}
              {server?.server_received_at_iso && (
                <div>server_received: {new Date(server.server_received_at_iso).toLocaleTimeString("en-GB")}</div>
              )}
              {server?.reason_codes?.length > 0 && (
                <div>reason: {server.reason_codes.join(" · ")}</div>
              )}
            </div>
          </div>
        </div>
      </div>
      <button
        data-testid="btn-result-done"
        onClick={onDone}
        className="mt-6 w-full h-14 bg-black text-white font-mono text-sm uppercase tracking-wider"
      >
        Done
      </button>
    </div>
  );
}

function QueueStep({ queue, onRetry, onDelete, onClearSynced, onClose }) {
  return (
    <div className="p-4">
      <div className="flex items-center justify-between">
        <h2 className="font-heading text-2xl font-black tracking-tighter">Local queue</h2>
        <div className="flex gap-2">
          <button data-testid="btn-clear-synced" onClick={onClearSynced} className="h-9 px-3 border border-neutral-300 font-mono text-xs uppercase tracking-wider">
            Clear synced
          </button>
          <button onClick={onClose} className="h-9 px-3 border border-neutral-300 font-mono text-xs uppercase tracking-wider">
            Close
          </button>
        </div>
      </div>
      <p className="text-neutral-600 text-sm mt-2">
        Every capture is stored here until People Engine confirms receipt. Decisions live in People Engine.
      </p>
      <div className="mt-4 space-y-2" data-testid="queue-list">
        {queue.length === 0 && (
          <div className="border border-neutral-200 bg-[#FAFAFA] p-6 text-center mono-label">
            Queue empty.
          </div>
        )}
        {queue.map((r) => (
          <div key={r.local_id} className="border border-neutral-200 bg-white p-3" data-testid={`queue-row-${r.local_id}`}>
            <div className="flex items-center gap-2">
              <StatusPill status={r.sync_status} />
              <span className="mono-label" style={{ fontSize: 10 }}>{r.attendance_type.replace("_", " ")}</span>
              <span className="mono-label ml-auto" style={{ fontSize: 10 }}>
                {new Date(r.captured_at_device).toLocaleString("en-GB")}
              </span>
            </div>
            <div className="mt-2 font-mono text-[11px] text-neutral-700 break-all">
              <div>local_id: {r.local_id}</div>
              <div>employee: {r.employee_id}</div>
              {r.gps_lat != null && <div>gps: {r.gps_lat.toFixed(4)}, {r.gps_lng.toFixed(4)} ±{Math.round(r.gps_accuracy_m || 0)}m</div>}
              {r.last_error && <div className="text-red-700">error: {r.last_error}</div>}
              {r.server_response?.decision && <div>server: {r.server_response.decision}</div>}
            </div>
            <div className="mt-2 flex gap-2">
              {r.sync_status !== "SYNCED" && (
                <button data-testid={`retry-${r.local_id}`} onClick={() => onRetry(r.local_id)} className="h-8 px-3 border border-neutral-900 font-mono text-[10px] uppercase tracking-wider inline-flex items-center gap-1">
                  <RefreshCw size={12} /> Retry
                </button>
              )}
              <button data-testid={`delete-${r.local_id}`} onClick={() => onDelete(r.local_id)} className="h-8 px-3 border border-red-500 text-red-700 font-mono text-[10px] uppercase tracking-wider inline-flex items-center gap-1">
                <Trash2 size={12} /> Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    SYNCED: "border-[#22C55E] bg-[#DCFCE7] text-[#166534]",
    PENDING: "border-[#EAB308] bg-[#FEF9C3] text-[#854D0E]",
    FAILED: "border-[#EF4444] bg-[#FEE2E2] text-[#991B1B]",
  };
  return <span className={`px-2 py-0.5 border font-mono text-[10px] uppercase tracking-wider ${map[status] || "border-neutral-300"}`}>{status}</span>;
}

function CornerBrackets() {
  const c = "absolute w-5 h-5 border-white";
  return (
    <>
      <span className={`${c} top-2 left-2 border-l-2 border-t-2`} />
      <span className={`${c} top-2 right-2 border-r-2 border-t-2`} />
      <span className={`${c} bottom-2 left-2 border-l-2 border-b-2`} />
      <span className={`${c} bottom-2 right-2 border-r-2 border-b-2`} />
    </>
  );
}
